import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  CogIcon,
  ServerIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { PontBascule, ScaleAgentConfig } from '../types/scaleAgent';
import { pontBasculeService } from '../services/pontBasculeService';
import sitesService, { Site, Tenant } from '../services/sites';

// Type pour la création d'un pont bascule (sans id et code)
type PontBasculeCreateData = Omit<PontBascule, 'id' | 'code' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>;

import { validateScaleAgentConfig } from '../config/scaleAgent';
import AgentConfigModal from '../components/pontBascule/AgentConfigModal';

interface PontBasculeFormData {
  code: string;
  name: string;
  tenantId: string;
  siteId: string;
  status: 'active' | 'inactive' | 'maintenance';
  location: string;
  description: string;
  isDefault: boolean;
  
  // Configuration Scale Agent
  wsUrl: string;
  port: number;
  scaleUnit: string;
  scalePrecision: number;
  stabilityThreshold: number;
  
  // Configuration de reconnexion
  reconnectEnabled: boolean;
  reconnectMaxAttempts: number;
  reconnectDelay: number;
  
  // Configuration reseau
  networkIpAddress: string;
  networkSubnet: string;
  networkGateway: string;
  networkDns: string;
  
  // Configuration de securite
  apiKey: string;
  allowedOrigins: string[];
  requireAuthentication: boolean;
  
  // Nested configs for compatibility
  scaleAgentConfig: {
    port: number;
    scale: {
      unit: string;
      precision: number;
      stabilityThreshold: number;
    };
    reconnect: {
      enabled: boolean;
      maxAttempts: number;
      delay: number;
    };
  };
  networkConfig: {
    ipAddress: string;
    subnet: string;
    gateway: string;
    dns: string;
  };
  securityConfig: {
    apiKey: string;
    requireAuthentication: boolean;
    allowedOrigins: string[];
  };
}

const PontsBasculeConfigPage: React.FC = () => {
  const navigate = useNavigate();
  
  // États
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [pontsBascule, setPontsBascule] = useState<PontBascule[]>([]);
  const [loading, setLoading] = useState(false);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPont, setEditingPont] = useState<PontBascule | null>(null);
  const [showAgentConfigModal, setShowAgentConfigModal] = useState(false);
  const [selectedPontForConfig, setSelectedPontForConfig] = useState<PontBascule | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedPontForTest, setSelectedPontForTest] = useState<PontBascule | null>(null);
  const [formData, setFormData] = useState<PontBasculeFormData>({
    code: '', // Sera genere par le serveur
    name: '',
    tenantId: '',
    siteId: '',
    status: 'active',
    location: '',
    description: '',
    isDefault: false,
    
    // Configuration Scale Agent
    wsUrl: 'ws://127.0.0.1:9090/weight',
    port: 9090,
    scaleUnit: 'kg',
    scalePrecision: 2,
    stabilityThreshold: 0.01,
    
    // Configuration de reconnexion
    reconnectEnabled: true,
    reconnectMaxAttempts: 5,
    reconnectDelay: 1000,
    
    // Configuration reseau
    networkIpAddress: '127.0.0.1',
    networkSubnet: '255.255.255.0',
    networkGateway: '192.168.1.1',
    networkDns: '8.8.8.8',
    
    // Configuration de securite
    apiKey: '',
    allowedOrigins: ['127.0.0.1', 'localhost'],
    requireAuthentication: true,
    
    // Nested configs for compatibility
    scaleAgentConfig: {
      port: 9090,
      scale: {
        unit: 'kg',
        precision: 2,
        stabilityThreshold: 0.01,
      },
      reconnect: {
        enabled: true,
        maxAttempts: 5,
        delay: 1000,
      },
    },
    networkConfig: {
      ipAddress: '127.0.0.1',
      subnet: '255.255.255.0',
      gateway: '192.168.1.1',
      dns: '8.8.8.8',
    },
    securityConfig: {
      apiKey: '',
      requireAuthentication: true,
      allowedOrigins: ['127.0.0.1', 'localhost'],
    },
  });

  // Charger les tenants et sites
  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      loadSitesByTenant(selectedTenant);
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedSite) {
      loadPontsBasculeBySite(selectedSite);
    }
  }, [selectedSite]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      
      // Essayer d'abord l'endpoint dédié aux tenants
      let tenantsData = await sitesService.getTenants();
      
      // Si pas de données ou erreur, essayer de récupérer depuis les sites
      if (!tenantsData || !Array.isArray(tenantsData) || tenantsData.length === 0) {
        try {
          const allSites = await sitesService.getSites();
          
          if (Array.isArray(allSites) && allSites.length > 0) {
            // Extraire les tenants uniques depuis les sites
            const uniqueTenants = allSites.reduce((acc: Tenant[], site: Site) => {
              const existingTenant = acc.find(t => t.id === site.tenant.id);
              if (!existingTenant) {
                // Créer un objet Tenant complet avec les propriétés manquantes
                const tenant: Tenant = {
                  ...site.tenant,
                  is_active: true // Valeur par défaut
                };
                acc.push(tenant);
              }
              return acc;
            }, []);
            
            tenantsData = uniqueTenants;
          }
        } catch (siteError) {
          console.error('Erreur lors de la récupération des sites:', siteError);
        }
      }
      
      // Vérifier que tenantsData est un tableau
      if (Array.isArray(tenantsData)) {
        setTenants(tenantsData);
        if (tenantsData.length > 0) {
          setSelectedTenant(tenantsData[0].id.toString());
        }
      } else {
        console.error('Tenants data n\'est pas un tableau:', tenantsData);
        setTenants([]);
      }
    } catch (error) {
      console.error('Erreur chargement tenants:', error);
      setTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadSitesByTenant = async (tenantId: string) => {
    try {
      const allSites = await sitesService.getSites();
      const tenantSites = allSites.filter((site: Site) => site.tenant.id.toString() === tenantId);
      setSites(tenantSites);
      if (tenantSites.length > 0) {
        setSelectedSite(tenantSites[0].id.toString());
      } else {
        setSelectedSite('');
        setPontsBascule([]);
      }
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const loadPontsBasculeBySite = async (siteId: string) => {
    try {
      setLoading(true);
      const ponts = await pontBasculeService.getPontsBasculeBySite(siteId);
      setPontsBascule(ponts);
    } catch (error) {
      console.error('Erreur chargement ponts bascule:', error);
      setPontsBascule([]);
    } finally {
      setLoading(false);
    }
  };

  // Gérer les changements de formulaire
  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Le code unique est maintenant généré automatiquement par le serveur

     // Ouvrir le formulaire d'ajout
   const handleAddPont = () => {
     setEditingPont(null);
           setFormData({
        code: '', // Sera genere par le serveur
        name: '',
        tenantId: selectedTenant,
        siteId: selectedSite,
        status: 'active',
        location: '',
        description: '',
        isDefault: false,
        
        // Configuration Scale Agent
        wsUrl: 'ws://127.0.0.1:9090/weight',
        port: 9090,
        scaleUnit: 'kg',
        scalePrecision: 2,
        stabilityThreshold: 0.01,
        
        // Configuration de reconnexion
        reconnectEnabled: true,
        reconnectMaxAttempts: 5,
        reconnectDelay: 1000,
        
        // Configuration reseau
        networkIpAddress: '127.0.0.1',
        networkSubnet: '255.255.255.0',
        networkGateway: '192.168.1.1',
        networkDns: '8.8.8.8',
        
        // Configuration de securite
        apiKey: '',
        allowedOrigins: ['127.0.0.1', 'localhost'],
        requireAuthentication: true,
        
        // Nested configs for compatibility
        scaleAgentConfig: {
          port: 9090,
          scale: {
            unit: 'kg',
            precision: 2,
            stabilityThreshold: 0.01,
          },
          reconnect: {
            enabled: true,
            maxAttempts: 5,
            delay: 1000,
          },
        },
        networkConfig: {
          ipAddress: '127.0.0.1',
          subnet: '255.255.255.0',
          gateway: '192.168.1.1',
          dns: '8.8.8.8',
        },
        securityConfig: {
          apiKey: '',
          requireAuthentication: true,
          allowedOrigins: ['127.0.0.1', 'localhost'],
        },
      });
    setShowForm(true);
  };

  // Ouvrir le formulaire d'édition
  const handleEditPont = (pont: PontBascule) => {
    setEditingPont(pont);
    setFormData({
      code: pont.code,
      name: pont.name,
      tenantId: pont.tenantId,
      siteId: pont.siteId,
      status: pont.status,
      location: pont.location || '',
      description: pont.description || '',
      isDefault: pont.is_default,
      
      // Configuration Scale Agent
      wsUrl: pont.wsUrl || 'ws://127.0.0.1:9090/weight',
      port: pont.port || 9090,
      scaleUnit: pont.scaleUnit || 'kg',
      scalePrecision: pont.scalePrecision || 2,
      stabilityThreshold: pont.stabilityThreshold || 0.01,
      
      // Configuration de reconnexion
      reconnectEnabled: pont.reconnectEnabled || true,
      reconnectMaxAttempts: pont.reconnectMaxAttempts || 5,
      reconnectDelay: pont.reconnectDelay || 1000,
      
      // Configuration reseau
      networkIpAddress: pont.networkIpAddress || '127.0.0.1',
      networkSubnet: pont.networkSubnet || '255.255.255.0',
      networkGateway: pont.networkGateway || '192.168.1.1',
      networkDns: pont.networkDns || '8.8.8.8',
      
      // Configuration de securite
      apiKey: pont.apiKey || '',
      allowedOrigins: pont.allowedOrigins || ['127.0.0.1', 'localhost'],
      requireAuthentication: pont.requireAuthentication || true,
      
      // Nested configs for compatibility
      scaleAgentConfig: {
        port: pont.port || 9090,
        scale: {
          unit: pont.scaleUnit || 'kg',
          precision: pont.scalePrecision || 2,
          stabilityThreshold: pont.stabilityThreshold || 0.01,
        },
        reconnect: {
          enabled: pont.reconnectEnabled || true,
          maxAttempts: pont.reconnectMaxAttempts || 5,
          delay: pont.reconnectDelay || 1000,
        },
      },
      networkConfig: {
        ipAddress: pont.networkIpAddress || '127.0.0.1',
        subnet: pont.networkSubnet || '255.255.255.0',
        gateway: pont.networkGateway || '192.168.1.1',
        dns: pont.networkDns || '8.8.8.8',
      },
      securityConfig: {
        apiKey: pont.apiKey || '',
        requireAuthentication: pont.requireAuthentication || true,
        allowedOrigins: pont.allowedOrigins || ['127.0.0.1', 'localhost'],
      },
    });
    setShowForm(true);
  };

  // Sauvegarder le pont bascule
  const handleSavePont = async () => {
    try {
      // Validation du Scale Agent uniquement lors de l'édition (pas lors de la création)
      if (editingPont) {
        // Créer un objet ScaleAgentConfig complet pour la validation
        const configToValidate: ScaleAgentConfig = {
          wsUrl: formData.wsUrl,
          port: formData.scaleAgentConfig.port,
          pontBasculeName: formData.name,
          siteId: formData.siteId,
          pontBasculeId: editingPont.id,
          reconnect: formData.scaleAgentConfig.reconnect,
          scale: formData.scaleAgentConfig.scale,
        };
        
        const scaleAgentValidation = validateScaleAgentConfig(configToValidate);
        if (!scaleAgentValidation.valid) {
          alert(`Erreurs de validation Scale Agent:\n${scaleAgentValidation.errors.join('\n')}`);
          return;
        }
      }

      // Mettre à jour la configuration avec les données du formulaire
      const configToSave: ScaleAgentConfig = {
        wsUrl: formData.wsUrl,
        port: formData.port,
        pontBasculeName: formData.name,
        siteId: formData.siteId,
        pontBasculeId: editingPont ? editingPont.id : '', // ID existant ou vide pour nouvelle création
        reconnect: {
          enabled: formData.reconnectEnabled,
          maxAttempts: formData.reconnectMaxAttempts,
          delay: formData.reconnectDelay,
        },
        scale: {
          unit: formData.scaleUnit,
          precision: formData.scalePrecision,
          stabilityThreshold: formData.stabilityThreshold,
        },
      };

      if (editingPont) {
        // Mise à jour - le code ne peut pas être modifié
        await pontBasculeService.updatePontBascule(editingPont.id, {
          name: formData.name,
          tenantId: formData.tenantId,
          siteId: formData.siteId,
          status: formData.status,
          location: formData.location,
          description: formData.description,
          is_default: formData.isDefault,
          scaleAgentConfig: configToSave,
          networkConfig: formData.networkConfig,
          securityConfig: formData.securityConfig,
        });
      } else {
        // Création - le code sera généré automatiquement par le serveur
        const createData: PontBasculeCreateData = {
          name: formData.name,
          tenantId: formData.tenantId,
          siteId: formData.siteId,
          status: formData.status,
          location: formData.location,
          description: formData.description,
          is_default: formData.isDefault,
          scaleAgentConfig: configToSave,
          networkConfig: formData.networkConfig,
          securityConfig: formData.securityConfig,
        };
        
        const newPont = await pontBasculeService.createPontBascule(createData);
        
        // Afficher le code généré par le serveur
        if (newPont && newPont.code) {
          alert(`Pont bascule créé avec succès !\nCode unique généré : ${newPont.code}`);
        }
      }

      // Recharger et fermer le formulaire
      await loadPontsBasculeBySite(selectedSite);
      setShowForm(false);
      setEditingPont(null);
    } catch (error) {
      console.error('Erreur sauvegarde pont bascule:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un pont bascule
  const handleDeletePont = async (pontId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce pont bascule ?')) {
      try {
        await pontBasculeService.deletePontBascule(pontId);
        await loadPontsBasculeBySite(selectedSite);
      } catch (error) {
        console.error('Erreur suppression pont bascule:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  // Définir comme pont par défaut
  const handleSetDefault = async (pontId: string) => {
    try {
      // Mettre à jour tous les ponts du site pour désactiver le statut par défaut
      const updatedPonts = pontsBascule.map(pont => ({
        ...pont,
        isDefault: pont.id === pontId
      }));

      // Sauvegarder les modifications
      for (const pont of updatedPonts) {
        await pontBasculeService.updatePontBascule(pont.id, {
          is_default: pont.isDefault
        });
      }

      // Recharger les ponts
      await loadPontsBasculeBySite(selectedSite);
    } catch (error) {
      console.error('Erreur mise à jour pont par défaut:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  // Tester la connexion et afficher les informations
  const handleTestConnection = async (pont: PontBascule) => {
    try {
      setSelectedPontForTest(pont);
      setShowTestModal(true);
    } catch (error) {
      console.error('Erreur test connexion:', error);
      alert('Erreur lors de la récupération des informations du pont bascule');
    }
  };

  // Ouvrir le modal de configuration de l'agent
  const handleOpenAgentConfig = (pont: PontBascule) => {
    setSelectedPontForConfig(pont);
    setShowAgentConfigModal(true);
    // Fermer le modal de test si il est ouvert
    if (showTestModal) {
      setShowTestModal(false);
      setSelectedPontForTest(null);
    }
  };

  // Gérer la mise à jour de la configuration de l'agent
  const handleAgentConfigUpdate = async (returnData: any) => {
    if (selectedPontForConfig) {
      try {
        // Détecter le type de fichier reçu
        if ('networkInfo' in returnData && returnData.networkInfo) {
          // C'est un fichier de retour d'état (retour de l'agent)
          console.log('📊 Traitement d\'un fichier de retour d\'état');
          
          const currentNetworkConfig = selectedPontForConfig.networkConfig || {
            ipAddress: '127.0.0.1',
            subnet: '255.255.255.0',
            gateway: '192.168.1.1',
            dns: '8.8.8.8'
          };
          
          await pontBasculeService.updatePontBascule(selectedPontForConfig.id, {
            networkConfig: {
              ...currentNetworkConfig,
              localIp: returnData.networkInfo.localIp,
              macAddress: returnData.networkInfo.macAddress,
              hostname: returnData.networkInfo.hostname,
              osInfo: returnData.networkInfo.osInfo,
            },
            // Ajouter d'autres informations si nécessaire
          });
          
          alert('Retour d\'état de l\'agent traité avec succès !');
          
        } else if ('configuration' in returnData && returnData.configuration) {
          // C'est un fichier de configuration d'agent (envoyé par l'agent)
          console.log('📋 Traitement d\'un fichier de configuration d\'agent');
          
          const config = returnData.configuration;
          
          // Vérifier que la configuration correspond au pont bascule actuel
          if (config.pontBasculeId !== selectedPontForConfig.id) {
            alert('⚠️ Attention: La configuration reçue ne correspond pas au pont bascule sélectionné');
            return;
          }
          
          // Mettre à jour la configuration du pont bascule avec les données reçues
          await pontBasculeService.updatePontBascule(selectedPontForConfig.id, {
            scaleAgentConfig: {
              wsUrl: config.wsUrl,
              port: config.port,
              pontBasculeName: selectedPontForConfig.name,
              siteId: selectedPontForConfig.siteId,
              pontBasculeId: selectedPontForConfig.id,
              reconnect: selectedPontForConfig.scaleAgentConfig?.reconnect || { enabled: true, maxAttempts: 5, delay: 1000 },
              scale: selectedPontForConfig.scaleAgentConfig?.scale || { unit: 'kg', precision: 2, stabilityThreshold: 0.01 }
            },
            networkConfig: {
              ipAddress: config.network?.ipAddress || '127.0.0.1',
              subnet: '255.255.255.0',
              gateway: '192.168.1.1',
              dns: '8.8.8.8',
              macAddress: config.network?.macAddress,
              hostname: config.network?.hostname,
            },
          });
          
          alert('Configuration de l\'agent reçue et traitée avec succès !');
          
        } else {
          throw new Error('Format de fichier non reconnu');
        }
        
        // Recharger les ponts bascule
        await loadPontsBasculeBySite(selectedSite);
        
      } catch (error) {
        console.error('Erreur mise à jour configuration agent:', error);
        alert(`Erreur lors de la mise à jour de la configuration: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      }
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* En-tête avec bouton de retour */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Configuration des Ponts Bascule</h1>
                <p className="mt-2 text-gray-600">
                  Gérez les ponts bascule et leurs configurations Scale Agent par tenant et site
                </p>
              </div>
            </div>



          {/* Sélecteurs Tenant et Site */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">Tenant</label>
                 <select
                   value={selectedTenant}
                   onChange={(e) => setSelectedTenant(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   disabled={tenantsLoading}
                 >
                   <option value="">
                     {tenantsLoading ? 'Chargement...' : 'Sélectionner un tenant'}
                   </option>
                   {Array.isArray(tenants) && tenants.map((tenant) => (
                     <option key={tenant.id} value={tenant.id}>
                       {tenant.name} ({tenant.code})
                     </option>
                   ))}
                 </select>
                 {tenantsLoading && (
                   <p className="text-xs text-gray-500 mt-1">Chargement des tenants...</p>
                 )}
               </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site</label>
                <select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  disabled={!selectedTenant}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Sélectionner un site</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name} ({site.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bouton d'ajout */}
        {selectedSite && (
          <div className="mb-6">
            <button
              onClick={handleAddPont}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Ajouter un Pont Bascule
            </button>
          </div>
        )}

        {/* Liste des ponts bascule */}
        {selectedSite && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Chargement des ponts bascule...</p>
              </div>
            ) : pontsBascule.length === 0 ? (
              <div className="p-8 text-center">
                <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun pont bascule</h3>
                <p className="mt-1 text-sm text-gray-500">Commencez par ajouter votre premier pont bascule pour ce site.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {pontsBascule.map((pont) => (
                  <li key={pont.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-medium text-gray-900">{pont.name}</h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {pont.code}
                            </span>
                            {pont.is_default && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Par défaut
                              </span>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            pont.status === 'active' ? 'bg-green-100 text-green-800' :
                            pont.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {pont.status}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          <p><strong>Localisation:</strong> {pont.location || 'Non spécifiée'}</p>
                          <p><strong>Scale Agent:</strong> {pont.scaleAgentConfig?.wsUrl || 'Non configuré'}</p>
                          <p><strong>Port:</strong> {pont.scaleAgentConfig?.port || 'Non configuré'}</p>
                          <p><strong>Unité:</strong> {pont.scaleAgentConfig?.scale?.unit || 'Non configuré'}</p>
                          <p><strong>Précision:</strong> {pont.scaleAgentConfig?.scale?.precision || 'Non configuré'} décimales</p>
                        </div>
                      </div>
                                             <div className="flex items-center space-x-2">
                         <button
                           onClick={() => handleOpenAgentConfig(pont)}
                           className="inline-flex items-center px-3 py-1 border border-purple-300 shadow-sm text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                         >
                           <CogIcon className="h-4 w-4 mr-1" />
                           Agent
                         </button>
                         <button
                           onClick={() => handleTestConnection(pont)}
                           className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                         >
                           Tester
                         </button>
                                                 {!pont.is_default && (
                          <button
                            onClick={() => handleSetDefault(pont.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            Définir par défaut
                          </button>
                        )}
                        <button
                          onClick={() => handleEditPont(pont)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeletePont(pont.id)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Modal de formulaire */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingPont ? 'Modifier le Pont Bascule' : 'Nouveau Pont Bascule'}
                  </h3>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form className="space-y-6">
                                     {/* Code unique */}
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Code unique</label>
                     <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                       <p className="text-sm text-gray-600">
                         {editingPont ? (
                           <span className="font-mono text-blue-600">{formData.code}</span>
                         ) : (
                           "Le code unique sera généré automatiquement par le serveur lors de l'enregistrement"
                         )}
                       </p>
                       {editingPont && (
                         <p className="text-xs text-gray-500 mt-1">
                           Le code unique ne peut pas être modifié après création
                         </p>
                       )}
                     </div>
                   </div>

                  {/* Informations générales */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Nom du Pont Bascule</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="ex: Pont Principal"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Statut</label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleFormChange('status', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Localisation</label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleFormChange('location', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="ex: Zone d'entrée, Quai 1, etc."
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.isDefault}
                        onChange={(e) => handleFormChange('isDefault', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="text-sm text-gray-700">Pont par défaut pour ce site</label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Description détaillée du pont bascule..."
                    />
                  </div>

                  {/* Configuration Réseau */}
                  <div className="border-t pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <ServerIcon className="h-4 w-4 mr-2" />
                      Configuration Réseau
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Adresse IP</label>
                        <input
                          type="text"
                                          value={formData.networkIpAddress}
                onChange={(e) => handleFormChange('networkIpAddress', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="192.168.1.100"
                        />
                        <p className="text-xs text-gray-500 mt-1">Adresse IP du Scale Agent local</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Masque de sous-réseau</label>
                        <input
                          type="text"
                                          value={formData.networkSubnet}
                onChange={(e) => handleFormChange('networkSubnet', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="255.255.255.0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Passerelle</label>
                        <input
                          type="text"
                                          value={formData.networkGateway}
                onChange={(e) => handleFormChange('networkGateway', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="192.168.1.1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Serveur DNS</label>
                        <input
                          type="text"
                                          value={formData.networkDns}
                onChange={(e) => handleFormChange('networkDns', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="8.8.8.8"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Configuration Scale Agent */}
                  <div className="border-t pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <CogIcon className="h-4 w-4 mr-2" />
                      Configuration Scale Agent
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">URL WebSocket</label>
                        <input
                          type="text"
                                          value={formData.wsUrl}
                onChange={(e) => handleFormChange('wsUrl', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="ws://192.168.1.100:9090/weight"
                        />
                        <p className="text-xs text-gray-500 mt-1">Format: ws://IP:PORT/weight</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Port</label>
                        <input
                          type="number"
                          value={formData.scaleAgentConfig.port}
                          onChange={(e) => handleFormChange('scaleAgentConfig.port', parseInt(e.target.value))}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          min="1"
                          max="65535"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ports recommandés: 8080, 9090, 8081, 9091</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Unité</label>
                        <select
                          value={formData.scaleAgentConfig.scale.unit}
                          onChange={(e) => handleFormChange('scaleAgentConfig.scale.unit', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="kg">Kilogrammes (kg)</option>
                          <option value="lb">Livres (lb)</option>
                          <option value="t">Tonnes (t)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Précision</label>
                        <input
                          type="number"
                          value={formData.scaleAgentConfig.scale.precision}
                          onChange={(e) => handleFormChange('scaleAgentConfig.scale.precision', parseInt(e.target.value))}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          min="0"
                          max="6"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Seuil de Stabilité</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.scaleAgentConfig.scale.stabilityThreshold}
                          onChange={(e) => handleFormChange('scaleAgentConfig.scale.stabilityThreshold', parseFloat(e.target.value))}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          min="0.001"
                        />
                      </div>
                    </div>

                    {/* Configuration de reconnexion */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-md">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Configuration de Reconnexion</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.scaleAgentConfig.reconnect.enabled}
                            onChange={(e) => handleFormChange('scaleAgentConfig.reconnect.enabled', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-900">Activer la reconnexion</label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Tentatives max</label>
                          <input
                            type="number"
                            value={formData.scaleAgentConfig.reconnect.maxAttempts}
                            onChange={(e) => handleFormChange('scaleAgentConfig.reconnect.maxAttempts', parseInt(e.target.value))}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            min="0"
                            max="20"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Délai (ms)</label>
                          <input
                            type="number"
                            value={formData.scaleAgentConfig.reconnect.delay}
                            onChange={(e) => handleFormChange('scaleAgentConfig.reconnect.delay', parseInt(e.target.value))}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            min="100"
                            step="100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Configuration de Sécurité */}
                  <div className="border-t pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <ShieldCheckIcon className="h-4 w-4 mr-2" />
                      Configuration de Sécurité
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Clé API</label>
                        <input
                          type="text"
                          value={formData.securityConfig.apiKey}
                          onChange={(e) => handleFormChange('securityConfig.apiKey', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="votre-clé-secrète-ici"
                        />
                        <p className="text-xs text-gray-500 mt-1">Clé d'authentification pour l'API</p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.securityConfig.requireAuthentication}
                          onChange={(e) => handleFormChange('securityConfig.requireAuthentication', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">Authentification requise</label>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">Origines autorisées</label>
                      <textarea
                        value={formData.securityConfig.allowedOrigins.join('\n')}
                        onChange={(e) => handleFormChange('securityConfig.allowedOrigins', e.target.value.split('\n').filter(origin => origin.trim()))}
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="127.0.0.1&#10;localhost&#10;192.168.1.100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Une origine par ligne (IPs ou noms d'hôte autorisés)</p>
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePont}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {editingPont ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
         )}

         {/* Modal de configuration de l'agent */}
         {showAgentConfigModal && selectedPontForConfig && (
           <AgentConfigModal
             isOpen={showAgentConfigModal}
             onClose={() => {
               setShowAgentConfigModal(false);
               setSelectedPontForConfig(null);
             }}
             pontBascule={selectedPontForConfig}
                           tenant={{
                id: parseInt(selectedPontForConfig.tenant_id || selectedPontForConfig.tenantId || '0'),
                name: selectedPontForConfig.tenant_name || 'N/A',
                code: selectedPontForConfig.tenant_code || 'N/A',
                is_active: true
              }}
              site={{
                id: parseInt(selectedPontForConfig.site_id || selectedPontForConfig.siteId || '0'),
                name: selectedPontForConfig.site_name || 'N/A',
                code: selectedPontForConfig.site_code || 'N/A',
                site_type: 'default',
                address: 'N/A',
                is_active: true,
                tenant: {
                  id: parseInt(selectedPontForConfig.tenant_id || selectedPontForConfig.tenantId || '0'),
                  name: selectedPontForConfig.tenant_name || 'N/A',
                  code: selectedPontForConfig.tenant_code || 'N/A'
                }
              }}
             onConfigUpdate={handleAgentConfigUpdate}
           />
         )}

         {/* Modal de test et informations du pont bascule */}
         {showTestModal && selectedPontForTest && (
           <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
             <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
               <div className="mt-3">
                 <div className="flex items-center justify-between mb-6">
                   <h3 className="text-xl font-medium text-gray-900">
                     Test et Informations - {selectedPontForTest.name}
                   </h3>
                   <button
                     onClick={() => {
                       setShowTestModal(false);
                       setSelectedPontForTest(null);
                     }}
                     className="text-gray-400 hover:text-gray-600"
                   >
                     <XMarkIcon className="h-6 w-6" />
                   </button>
                 </div>

                 <div className="space-y-6">
                   {/* Informations générales */}
                   <div className="bg-blue-50 p-4 rounded-lg">
                     <h4 className="text-lg font-medium text-blue-900 mb-3 flex items-center">
                       <CogIcon className="h-5 w-5 mr-2" />
                       Informations Générales
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <p><strong>Code:</strong> <span className="font-mono text-blue-600">{selectedPontForTest.code}</span></p>
                         <p><strong>Statut:</strong> 
                           <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                             selectedPontForTest.status === 'active' ? 'bg-green-100 text-green-800' :
                             selectedPontForTest.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                             'bg-red-100 text-red-800'
                           }`}>
                             {selectedPontForTest.status}
                           </span>
                         </p>
                         <p><strong>Localisation:</strong> {selectedPontForTest.location || 'Non spécifiée'}</p>
                         <p><strong>Description:</strong> {selectedPontForTest.description || 'Non spécifiée'}</p>
                                                   <p><strong>Pont par défaut:</strong> {selectedPontForTest.is_default ? 'Oui' : 'Non'}</p>
                       </div>
                       <div>
                         <p><strong>Tenant:</strong> {selectedPontForTest.tenant_name || selectedPontForTest.tenantId || 'N/A'}</p>
                         <p><strong>Site:</strong> {selectedPontForTest.site_name || selectedPontForTest.siteId || 'N/A'}</p>
                         <p><strong>Créé le:</strong> {selectedPontForTest.createdAt ? new Date(selectedPontForTest.createdAt).toLocaleDateString('fr-FR') : 'N/A'}</p>
                         <p><strong>Modifié le:</strong> {selectedPontForTest.updatedAt ? new Date(selectedPontForTest.updatedAt).toLocaleDateString('fr-FR') : 'N/A'}</p>
                       </div>
                     </div>
                   </div>

                   {/* Configuration Scale Agent */}
                   <div className="bg-green-50 p-4 rounded-lg">
                     <h4 className="text-lg font-medium text-green-900 mb-3 flex items-center">
                       <ServerIcon className="h-5 w-5 mr-2" />
                       Configuration Scale Agent
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <p><strong>URL WebSocket:</strong> 
                           <span className="font-mono text-green-600 ml-2">
                             {selectedPontForTest.scaleAgentConfig?.wsUrl || 'Non configuré'}
                           </span>
                         </p>
                         <p><strong>Port:</strong> {selectedPontForTest.scaleAgentConfig?.port || 'Non configuré'}</p>
                         <p><strong>Unité:</strong> {selectedPontForTest.scaleAgentConfig?.scale?.unit || 'Non configuré'}</p>
                         <p><strong>Précision:</strong> {selectedPontForTest.scaleAgentConfig?.scale?.precision || 'Non configuré'} décimales</p>
                         <p><strong>Seuil de stabilité:</strong> {selectedPontForTest.scaleAgentConfig?.scale?.stabilityThreshold || 'Non configuré'}</p>
                       </div>
                       <div>
                         <p><strong>Reconnexion activée:</strong> {selectedPontForTest.scaleAgentConfig?.reconnect?.enabled ? 'Oui' : 'Non'}</p>
                         <p><strong>Max tentatives:</strong> {selectedPontForTest.scaleAgentConfig?.reconnect?.maxAttempts || 'Non configuré'}</p>
                         <p><strong>Délai reconnexion:</strong> {selectedPontForTest.scaleAgentConfig?.reconnect?.delay || 'Non configuré'} ms</p>
                       </div>
                     </div>
                   </div>

                   {/* Configuration Réseau */}
                   <div className="bg-purple-50 p-4 rounded-lg">
                     <h4 className="text-lg font-medium text-purple-900 mb-3 flex items-center">
                       <ServerIcon className="h-5 w-5 mr-2" />
                       Configuration Réseau
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <p><strong>IP Réseau:</strong> {selectedPontForTest.networkConfig?.ipAddress || 'Non configuré'}</p>
                         <p><strong>Sous-réseau:</strong> {selectedPontForTest.networkConfig?.subnet || 'Non configuré'}</p>
                         <p><strong>Passerelle:</strong> {selectedPontForTest.networkConfig?.gateway || 'Non configuré'}</p>
                         <p><strong>DNS:</strong> {selectedPontForTest.networkConfig?.dns || 'Non configuré'}</p>
                       </div>
                       <div>
                         <p><strong>IP Agent Local:</strong> {selectedPontForTest.networkConfig?.localIp || 'Non configuré'}</p>
                         <p><strong>Adresse MAC:</strong> {selectedPontForTest.networkConfig?.macAddress || 'Non configuré'}</p>
                         <p><strong>Nom d'hôte:</strong> {selectedPontForTest.networkConfig?.hostname || 'Non configuré'}</p>
                         <p><strong>OS:</strong> {selectedPontForTest.networkConfig?.osInfo || 'Non configuré'}</p>
                       </div>
                     </div>
                   </div>

                   {/* Informations Agent */}
                   <div className="bg-yellow-50 p-4 rounded-lg">
                     <h4 className="text-lg font-medium text-yellow-900 mb-3 flex items-center">
                       <CogIcon className="h-5 w-5 mr-2" />
                       Informations Agent
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <p><strong>Version:</strong> {selectedPontForTest.agentVersion || 'Non configuré'}</p>
                          <p><strong>Plateforme:</strong> {selectedPontForTest.agentVersion || 'Non configuré'}</p>
                          <p><strong>Python:</strong> {selectedPontForTest.agentVersion || 'Non configuré'}</p>
                         <p><strong>Statut d'installation:</strong> {selectedPontForTest.agentInstallationStatus || 'Non configuré'}</p>
                       </div>
                       <div>
                         <p><strong>Statut réseau:</strong> {selectedPontForTest.agentNetworkStatus || 'Non configuré'}</p>
                         <p><strong>Statut service:</strong> {selectedPontForTest.agentServiceStatus || 'Non configuré'}</p>
                         <p><strong>Dernière communication:</strong> {selectedPontForTest.agentLastCommunication ? new Date(selectedPontForTest.agentLastCommunication).toLocaleString('fr-FR') : 'Non configuré'}</p>
                       </div>
                     </div>
                   </div>

                   {/* Configuration Sécurité */}
                   <div className="bg-red-50 p-4 rounded-lg">
                     <h4 className="text-lg font-medium text-red-900 mb-3 flex items-center">
                       <ShieldCheckIcon className="h-5 w-5 mr-2" />
                       Configuration Sécurité
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <p><strong>Clé API:</strong> 
                           <span className="font-mono text-red-600 ml-2">
                             {selectedPontForTest.securityConfig?.apiKey ? 'Configurée' : 'Non configurée'}
                           </span>
                         </p>
                         <p><strong>Authentification requise:</strong> {selectedPontForTest.securityConfig?.requireAuthentication ? 'Oui' : 'Non'}</p>
                         <p><strong>Origines autorisées:</strong> {selectedPontForTest.securityConfig?.allowedOrigins?.length || 0} origine(s)</p>
                       </div>
                       <div>
                         <p><strong>Clé secrète:</strong> 
                           <span className="font-mono text-red-600 ml-2">
                             {selectedPontForTest.apiSecretKey ? 'Configurée' : 'Non configurée'}
                           </span>
                         </p>
                       </div>
                     </div>
                   </div>

                   {/* Boutons d'action */}
                   <div className="flex justify-end space-x-3 pt-6 border-t">
                     <button
                       type="button"
                       onClick={() => {
                         setShowTestModal(false);
                         setSelectedPontForTest(null);
                       }}
                       className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                     >
                       Fermer
                     </button>
                     <button
                       type="button"
                       onClick={() => handleOpenAgentConfig(selectedPontForTest)}
                       className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                     >
                       Configurer l'Agent
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         )}
       </div>
     </div>
     </>
   );
 };

export default PontsBasculeConfigPage;
